import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { useVoice } from '../context/VoiceContext';
import { getWordsForActivity } from '../services/contentService';
import { logActivity, checkAndAwardAchievements } from '../services/activityService';
import { voiceSimilarity, MAX_VOICE_ATTEMPTS, PASS_THRESHOLD, speechSupported, ttsSupported } from '../utils/voiceUtils';
import './Activity.css';
import './Activity3D.css';

// ─── Component ────────────────────────────────────────────────
const SightWordsActivity = ({ onComplete }) => {
  const { currentUser } = useUser();
  const { microphoneGranted } = useVoice();

  // word list
  const [words, setWords]       = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completed, setCompleted] = useState(false);

  // TTS
  const [speechRate, setSpeechRate] = useState(1.0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // voice recording
  const [isRecording, setIsRecording]     = useState(false);
  const [voiceAttempts, setVoiceAttempts] = useState(0);
  const [voiceScore, setVoiceScore]       = useState(null);   // best score this word
  const [voiceFeedback, setVoiceFeedback] = useState('');
  const recognitionRef     = useRef(null);
  const autoAdvanceTimerRef = useRef(null);
  const watchdogTimerRef   = useRef(null);
  const nextWordRef         = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    setIsLoading(true);
    getWordsForActivity(currentUser.id, 'sight').then(({ data }) => {
      setWords(data || []);
      setIsLoading(false);
    });
  }, [currentUser]);

  // Clean up auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, []);

  const speechRateRef = React.useRef(speechRate);
  speechRateRef.current = speechRate;
  const hasNavigatedRef = React.useRef(false);

  useEffect(() => {
    if (!hasNavigatedRef.current || words.length === 0 || !ttsSupported) return;
    const timer = setTimeout(() => {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(words[currentIndex].word);
      utterance.rate = speechRateRef.current;
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // ── TTS ──────────────────────────────────────────────────────
  const playWord = () => {
    if (words.length === 0 || !ttsSupported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(words[currentIndex].word);
    utterance.rate  = speechRate;
    utterance.lang  = 'en-US';
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  // ── Voice recording ──────────────────────────────────────────
  const startRecording = () => {
    if (voiceAttempts >= MAX_VOICE_ATTEMPTS || isRecording) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    let resultHandled = false;
    const clearWatchdog = () => {
      if (watchdogTimerRef.current) { clearTimeout(watchdogTimerRef.current); watchdogTimerRef.current = null; }
    };

    recognition.onstart = () => setIsRecording(true);

    recognition.onresult = (event) => {
      resultHandled = true;
      clearWatchdog();
      const transcript   = event.results[0][0].transcript;
      const score        = voiceSimilarity(transcript, words[currentIndex].word);
      const newAttempts  = voiceAttempts + 1;
      const newBestScore = voiceScore === null ? score : Math.max(voiceScore, score);
      setVoiceAttempts(newAttempts);
      setVoiceScore(newBestScore);
      setIsRecording(false);

      const remaining = MAX_VOICE_ATTEMPTS - newAttempts;
      if (score >= PASS_THRESHOLD) {
        setVoiceFeedback(`Great job! Score: ${score}% ✅ You passed!`);
        // Log outcome immediately and auto-advance — only logged here (not in nextWord)
        logActivity(currentUser.id, 'sight', words[currentIndex].id, true, newAttempts, newBestScore);
        checkAndAwardAchievements(currentUser.id);
        autoAdvanceTimerRef.current = setTimeout(() => nextWordRef.current(), 1500);
      } else if (newAttempts >= MAX_VOICE_ATTEMPTS) {
        setVoiceFeedback(`Final score: ${score}%. Keep practising! 💪`);
        logActivity(currentUser.id, 'sight', words[currentIndex].id, false, newAttempts, newBestScore);
        checkAndAwardAchievements(currentUser.id);
        autoAdvanceTimerRef.current = setTimeout(() => nextWordRef.current(), 2000);
      } else {
        setVoiceFeedback(`Score: ${score}%. Keep trying! You need ${PASS_THRESHOLD}% to pass. (${remaining} attempt${remaining !== 1 ? 's' : ''} left)`);
      }
    };

    recognition.onerror = (event) => {
      resultHandled = true;
      clearWatchdog();
      if (event.error === 'aborted') { setIsRecording(false); return; }
      if (event.error === 'not-allowed' || event.error === 'audio-capture') {
        setVoiceFeedback('Microphone access denied. Please allow microphone access in your browser and try again.');
      } else if (event.error === 'no-speech') {
        setVoiceFeedback("I didn't hear anything — tap and say the word a bit louder.");
      } else if (event.error === 'network') {
        setVoiceFeedback('Voice check needs an internet connection right now. Please check your connection and try again.');
      } else {
        setVoiceFeedback("Couldn't hear you clearly. Please try again.");
      }
      setIsRecording(false);
    };

    // If recognition ends without ever producing a result or error, reset the
    // button so it doesn't stay stuck on "Listening...".
    recognition.onend = () => {
      clearWatchdog();
      if (!resultHandled) {
        setIsRecording(false);
        setVoiceFeedback("Didn't catch that — tap and try again.");
      }
    };

    try {
      recognition.start();
      // Watchdog: some browsers (notably desktop Chrome) can hang in the
      // listening state without ever firing onresult/onerror/onend. Force a
      // reset so the button never sticks on "Listening...".
      watchdogTimerRef.current = setTimeout(() => {
        if (resultHandled) return;
        resultHandled = true;
        try { recognition.abort(); } catch (e) { /* noop */ }
        setIsRecording(false);
        setVoiceFeedback("Voice check timed out — tap and try again.");
      }, 8000);
    } catch (err) {
      setIsRecording(false);
      setVoiceFeedback('Could not start the microphone. Please tap and try again.');
    }
  };

  const stopRecording = () => {
    if (watchdogTimerRef.current) { clearTimeout(watchdogTimerRef.current); watchdogTimerRef.current = null; }
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onstart = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsRecording(false);
  };

  // ── Next word ─────────────────────────────────────────────────
  // Logging is done in recognition.onresult at the point of outcome (pass/exhausted).
  // Clicking Next Word without recording skips the word without logging.
  const nextWord = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    window.speechSynthesis.cancel();
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onstart = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    if (currentIndex + 1 >= words.length) {
      setCompleted(true);
      return;
    }
    hasNavigatedRef.current = true;
    setCurrentIndex(currentIndex + 1);
    setVoiceAttempts(0);
    setVoiceScore(null);
    setVoiceFeedback('');
    setIsSpeaking(false);
    setIsRecording(false);
  };
  nextWordRef.current = nextWord;

  // ── Render states ─────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="activity-container activity-3d">
        <p style={{ textAlign: 'center', padding: '2rem' }}>
          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading words…
        </p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="activity-container activity-3d">
        <div className="activity-mascot celebrate" aria-hidden="true">
          <div className="am-body"></div>
          <div className="am-eye left"><div className="am-pupil"></div></div>
          <div className="am-eye right"><div className="am-pupil"></div></div>
          <div className="am-cheek left"></div>
          <div className="am-cheek right"></div>
          <div className="am-smile"></div>
        </div>
        <div className="activity-header">
          <h2><i className="fas fa-star" aria-hidden="true"></i> All Done!</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌟</div>
          <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Amazing work!
          </p>
          <p style={{ color: '#555', marginBottom: '2rem' }}>
            You finished all {words.length} sight word{words.length !== 1 ? 's' : ''}!
          </p>
          <button className="btn btn-accent" onClick={onComplete}>
            <i className="fas fa-chart-bar" aria-hidden="true"></i> See My Progress
          </button>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="activity-container activity-3d">
        <div className="activity-header">
          <h2><i className="fas fa-book-open" aria-hidden="true"></i> Sight Words Practice</h2>
        </div>
        <div className="activity-content">
          <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            No sight words assigned yet. Ask your teacher to assign words!
          </p>
        </div>
      </div>
    );
  }

  const passedVoice    = voiceScore !== null && voiceScore >= PASS_THRESHOLD;
  const voiceExhausted = voiceAttempts >= MAX_VOICE_ATTEMPTS;

  return (
    <div className="activity-container activity-3d">
      <div className="activity-mascot" aria-hidden="true">
        <div className="am-body"></div>
        <div className="am-eye left"><div className="am-pupil"></div></div>
        <div className="am-eye right"><div className="am-pupil"></div></div>
        <div className="am-cheek left"></div>
        <div className="am-cheek right"></div>
        <div className="am-smile"></div>
      </div>
      <div className="activity-header">
        <h2><i className="fas fa-book-open" aria-hidden="true"></i> Sight Words Practice</h2>
        <p>Listen to the word, then say it aloud!</p>
      </div>

      <div className="activity-content">

        {/* ── Word card ── */}
        <div className="word-display" aria-label={`Word: ${words[currentIndex].word}`}>
          {words[currentIndex].word}
        </div>

        {/* ── TTS controls ── */}
        <div className="controls">
          <button
            className="btn btn-secondary"
            onClick={playWord}
            disabled={isSpeaking || !ttsSupported}
            aria-label="Play word aloud"
          >
            <i className={isSpeaking ? 'fas fa-spinner fa-spin' : 'fas fa-volume-up'} aria-hidden="true"></i>
            {isSpeaking ? 'Speaking…' : 'Hear Word'}
          </button>


          <button
            className="btn btn-primary"
            onClick={playWord}
            disabled={isSpeaking || !ttsSupported}
            aria-label="Repeat word"
          >
            <i className="fas fa-redo" aria-hidden="true"></i> Repeat
          </button>

          <button className="btn btn-accent" onClick={nextWord}>
            <i className="fas fa-arrow-right" aria-hidden="true"></i> Next Word
          </button>
        </div>

        {!ttsSupported && (
          <p className="voice-not-supported">
            <i className="fas fa-info-circle" aria-hidden="true"></i> Text-to-speech is not supported in this browser.
          </p>
        )}

        {/* ── Speed slider ── */}
        {ttsSupported && (
          <div className="tts-speed-control">
            <label htmlFor="speech-rate" className="speed-label">
              <i className="fas fa-tachometer-alt" aria-hidden="true"></i>
              Speed: <strong>{speechRate.toFixed(1)}x</strong>
            </label>
            <input
              id="speech-rate"
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={speechRate}
              onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
              aria-label="Speech speed"
            />
            <span className="speed-hint">🐢 Slow &nbsp; Fast 🐇</span>
          </div>
        )}


        {/* ── Voice recording section ── */}
        <div className="voice-record-section">
          <p className="voice-section-title">
            <i className="fas fa-microphone" aria-hidden="true"></i> Your turn — say the word!
          </p>

          {speechSupported && microphoneGranted ? (
            <>
              <button
                className={`btn-record ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={(!isRecording && voiceExhausted) || passedVoice}
                aria-label={isRecording ? 'Stop recording' : 'Record your voice'}
              >
                <i
                  className={isRecording ? 'fas fa-stop recording-pulse' : 'fas fa-microphone'}
                  aria-hidden="true"
                ></i>
                {isRecording ? 'Listening… (tap to stop)' : passedVoice ? '✅ Passed!' : voiceExhausted ? 'Max attempts reached' : 'Say the Word'}
              </button>

              {voiceScore !== null && (
                <div className={`voice-score-badge ${voiceScore >= PASS_THRESHOLD ? 'pass' : voiceScore >= 40 ? 'medium' : 'low'}`}>
                  🎤 Voice Score: {voiceScore}%
                  {voiceScore >= PASS_THRESHOLD ? ' ✅ Passed!' : ` (need ${PASS_THRESHOLD}% to pass)`}
                </div>
              )}

              {voiceFeedback && (
                <div className="voice-feedback" role="status" aria-live="polite">
                  {voiceFeedback}
                </div>
              )}

              <p className="voice-attempts">
                Attempt {Math.min(voiceAttempts + (isRecording ? 1 : 0), MAX_VOICE_ATTEMPTS)} of {MAX_VOICE_ATTEMPTS}
                {passedVoice && ' — Great work! 🌟'}
              </p>

              <div className="voice-progress-bar" aria-hidden="true">
                {Array.from({ length: MAX_VOICE_ATTEMPTS }).map((_, i) => (
                  <div
                    key={i}
                    className={`voice-pip ${i < voiceAttempts ? 'used' : ''} ${i === voiceAttempts - 1 && passedVoice ? 'passed' : ''}`}
                  />
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                padding: '1rem',
                color: '#856404',
                marginBottom: '0.5rem',
              }}>
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>{' '}
                <strong>Voice recording requires Chrome or Edge browser.</strong>
                <br />
                <span style={{ fontSize: '0.85rem' }}>You can still use the "Hear Word" button and speed slider above.</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Word counter ── */}
        <div className="attempts" aria-live="polite">
          Word {currentIndex + 1} of {words.length}
        </div>
      </div>
    </div>
  );
};

export default SightWordsActivity;
